import { useEffect, useState } from 'react';
import {
  ChiTietPhieuKeToan,
  ChiTietPhieuKeToanResponse,
  PhieuKeToan,
  PhieuKeToanInput,
  PhieuKeToanResponse,
  PhieuKeToanSearchParams,
  ThuePhieuKeToan,
  ThuePhieuKeToanResponse
} from '@/types/schemas/phieu-ke-toan.type';
import { useAuth } from '@/contexts/auth-context';
import QUERY_KEYS from '@/constants/query-keys';
import api from '@/lib/api';

interface UsePhieuKeToanReturn {
  phieuKeToans: PhieuKeToan[];
  isLoading: boolean;
  isLoadingDetail: boolean;
  error: string | null;
  addPhieuKeToan: (newPhieuKeToan: PhieuKeToanInput) => Promise<PhieuKeToan>;
  updatePhieuKeToan: (uuid: string, updatedPhieuKeToan: PhieuKeToanInput) => Promise<PhieuKeToan>;
  deletePhieuKeToan: (uuid: string) => Promise<void>;
  refreshPhieuKeToans: () => Promise<void>;
  getPhieuKeToanByCode: (code: string) => Promise<PhieuKeToan | null>;
  getPhieuKeToanById: (uuid: string) => Promise<PhieuKeToan | null>;
  getPhieuKeToanDetail: (uuid: string) => Promise<ChiTietPhieuKeToan[]>;
  getPhieuKeToanByStatus: (status: string) => Promise<PhieuKeToan[]>;
  getPhieuKeToanByDateRange: (fromDate: string, toDate: string) => Promise<PhieuKeToan[]>;
  searchPhieuKeToan: (params: PhieuKeToanSearchParams) => Promise<PhieuKeToan[]>;
}

/**
 * Hook for managing PhieuKeToan (Accounting Voucher) data
 *
 * This hook provides functions to fetch, create, update, and delete accounting vouchers,
 * as well as manage their details and tax information.
 */
export const usePhieuKeToan = (initialPhieuKeToans: PhieuKeToan[] = []): UsePhieuKeToanReturn => {
  const [phieuKeToans, setPhieuKeToans] = useState<PhieuKeToan[]>(initialPhieuKeToans);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { entity } = useAuth();

  const fetchPhieuKeToans = async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.get<PhieuKeToanResponse>(`/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_KE_TOAN}/`);
      setPhieuKeToans(response.data.results);
      setError(null);
    } catch (error: any) {
      console.error('Error fetching accounting vouchers:', error);
      setError(error.message || 'Có lỗi xảy ra khi tải dữ liệu');
    } finally {
      setIsLoading(false);
    }
  };

  const addPhieuKeToan = async (newPhieuKeToan: PhieuKeToanInput): Promise<PhieuKeToan> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      const response = await api.post<PhieuKeToan>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_KE_TOAN}/`,
        newPhieuKeToan
      );
      setPhieuKeToans(prev => [...prev, response.data]);
      setError(null);
      return response.data;
    } catch (error: any) {
      console.error('Error adding accounting voucher:', error);
      setError(error.message || 'Có lỗi xảy ra khi thêm phiếu kế toán');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updatePhieuKeToan = async (uuid: string, updatedPhieuKeToan: PhieuKeToanInput): Promise<PhieuKeToan> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      const response = await api.put<PhieuKeToan>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_KE_TOAN}/${uuid}/`,
        updatedPhieuKeToan
      );
      setPhieuKeToans(prev => prev.map(item => (item.uuid === uuid ? response.data : item)));
      setError(null);
      return response.data;
    } catch (error: any) {
      console.error('Error updating accounting voucher:', error);
      setError(error.message || 'Có lỗi xảy ra khi cập nhật phiếu kế toán');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deletePhieuKeToan = async (uuid: string): Promise<void> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_KE_TOAN}/${uuid}/`);
      setPhieuKeToans(prev => prev.filter(item => item.uuid !== uuid));
      setError(null);
    } catch (error: any) {
      console.error('Error deleting accounting voucher:', error);
      setError(error.message || 'Có lỗi xảy ra khi xóa phiếu kế toán');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const getPhieuKeToanByCode = async (code: string): Promise<PhieuKeToan | null> => {
    if (!entity?.slug) throw new Error('Entity not found');

    try {
      const response = await api.get<PhieuKeToanResponse>(`/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_KE_TOAN}/`, {
        params: { so_ct: code }
      });
      return response.data.results.length > 0 ? response.data.results[0] : null;
    } catch (error) {
      console.error('Error fetching accounting voucher by code:', error);
      return null;
    }
  };

  const getPhieuKeToanById = async (uuid: string): Promise<PhieuKeToan | null> => {
    if (!entity?.slug) throw new Error('Entity not found');

    try {
      const response = await api.get<PhieuKeToan>(`/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_KE_TOAN}/${uuid}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching accounting voucher by ID:', error);
      return null;
    }
  };

  const getPhieuKeToanDetail = async (uuid: string): Promise<ChiTietPhieuKeToan[]> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoadingDetail(true);
    try {
      const response = await api.get<ChiTietPhieuKeToanResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_KE_TOAN}/${uuid}/chi-tiet-phieu-ke-toan/`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching accounting voucher detail:', error);
      return [];
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const getPhieuKeToanByStatus = async (status: string): Promise<PhieuKeToan[]> => {
    if (!entity?.slug) throw new Error('Entity not found');

    try {
      const response = await api.get<PhieuKeToanResponse>(`/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_KE_TOAN}/`, {
        params: { status }
      });
      return response.data.results;
    } catch (error) {
      console.error('Error fetching accounting vouchers by status:', error);
      return [];
    }
  };

  const getPhieuKeToanByDateRange = async (fromDate: string, toDate: string): Promise<PhieuKeToan[]> => {
    if (!entity?.slug) throw new Error('Entity not found');

    try {
      const response = await api.get<PhieuKeToanResponse>(`/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_KE_TOAN}/`, {
        params: {
          ngay_ct__gte: fromDate,
          ngay_ct__lte: toDate
        }
      });
      return response.data.results;
    } catch (error) {
      console.error('Error fetching accounting vouchers by date range:', error);
      return [];
    }
  };

  const searchPhieuKeToan = async (params: PhieuKeToanSearchParams): Promise<PhieuKeToan[]> => {
    if (!entity?.slug) throw new Error('Entity not found');

    try {
      const response = await api.get<PhieuKeToanResponse>(`/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_KE_TOAN}/`, {
        params
      });
      return response.data.results;
    } catch (error) {
      console.error('Error searching accounting vouchers:', error);
      return [];
    }
  };

  useEffect(() => {
    fetchPhieuKeToans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity?.slug]);

  return {
    phieuKeToans,
    isLoading,
    isLoadingDetail,
    error,
    addPhieuKeToan,
    updatePhieuKeToan,
    deletePhieuKeToan,
    refreshPhieuKeToans: fetchPhieuKeToans,
    getPhieuKeToanByCode,
    getPhieuKeToanById,
    getPhieuKeToanDetail,
    getPhieuKeToanByStatus,
    getPhieuKeToanByDateRange,
    searchPhieuKeToan
  };
};

export const fetchAllPhieuKeToan = async (
  entitySlug: string,
  params: Record<string, any> = {},
  pageSize = 200
): Promise<PhieuKeToan[]> => {
  let page = 1;
  let all: PhieuKeToan[] = [];
  let totalCount = 0;

  while (true) {
    const response = await api.get<PhieuKeToanResponse>(`/entities/${entitySlug}/erp/${QUERY_KEYS.PHIEU_KE_TOAN}/`, {
      params: {
        ...params,
        page,
        page_size: pageSize
      }
    });

    const pageResults = response.data.results || [];
    totalCount = response.data.count ?? totalCount;
    all = all.concat(pageResults);

    if (!pageResults.length) {
      break;
    }

    const hasNext = (response.data as any).next;

    if (!hasNext && pageResults.length < pageSize) {
      break;
    }

    if (totalCount && all.length >= totalCount) {
      break;
    }

    page += 1;
  }

  return all;
};
